import { Plus, X } from "lucide-react";
import {
  Button,
  Callout,
  FieldError,
  FieldHint,
  FieldLabel,
  IconButton,
  TextInput,
  formatDateTime,
  type ApiError,
} from "@amber/ui";
import {
  TIER_LABELS,
  breakEditText,
  type SourceEditBreak,
  type SourceEditChange,
  type SourceEditFields,
  type SourceEditView,
} from "../../contracts/index";

const HOURS = { priority: 2, standard: 12, low: 24 } as const;
const FOUND = { priority: "4 h", standard: "24 h", low: "48 h" } as const;
/** About 3 YouTube Data API units per channel scan (videos, Shorts and live listings). */
const YOUTUBE_UNITS_PER_SCAN = 3;

export function TierPreview({
  before,
  after,
  youtube,
}: {
  before: SourceEditFields;
  after: SourceEditFields;
  youtube: boolean;
}) {
  const perDay = (tier: keyof typeof HOURS) => 24 / HOURS[tier];
  const scans = perDay(after.cadence_tier);
  const changed = before.cadence_tier !== after.cadence_tier;
  return (
    <p className="m-0 mt-1.5 rounded-lg bg-surface-2 px-2.5 py-1.5 text-md text-fg" aria-live="polite">
      <b className="font-strong">
        {scans} scan{scans === 1 ? "" : "s"} a day
      </b>{" "}
      · new items found within {FOUND[after.cadence_tier]}
      {changed ? ` · now ${String(perDay(before.cadence_tier))} a day (${TIER_LABELS[before.cadence_tier]})` : ""}
      {youtube ? (
        <span className="block text-muted">
          Quota: about {scans * YOUTUBE_UNITS_PER_SCAN} YouTube Data API units a day
        </span>
      ) : null}
    </p>
  );
}

function BreakRow({
  value,
  index,
  onChange,
  onRemove,
}: {
  value: SourceEditBreak;
  index: number;
  onChange: (next: SourceEditBreak) => void;
  onRemove: () => void;
}) {
  if (value.kind === "expected_quiet") {
    return (
      <li className="rounded-lg border border-divider px-2.5 py-1.5 text-md">
        <span className="block text-fg">Expected quiet: {breakEditText(value)}</span>
        {value.to === null || value.ended ? (
          <label className="mt-1 flex items-center gap-2">
            <input
              type="checkbox"
              className="accent-[var(--color-link)]"
              checked={value.ended}
              onChange={(e) => {
                onChange({ ...value, ended: e.target.checked });
              }}
            />
            End this break today
          </label>
        ) : null}
      </li>
    );
  }
  const n = String(index + 1);
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_130px_130px_auto] items-center gap-1.5">
      <TextInput
        aria-label={`Break ${n} name`}
        placeholder="e.g. Summer recess"
        value={value.label}
        onChange={(e) => {
          onChange({ ...value, label: e.target.value });
        }}
      />
      <TextInput
        type="date"
        aria-label={`Break ${n} from`}
        value={value.from}
        onChange={(e) => {
          onChange({ ...value, from: e.target.value });
        }}
      />
      <TextInput
        type="date"
        aria-label={`Break ${n} to`}
        value={value.to ?? ""}
        onChange={(e) => {
          onChange({ ...value, to: e.target.value || null });
        }}
      />
      <IconButton label={`Remove break ${n}`} size="sm" onClick={onRemove}>
        <X size={13} aria-hidden="true" />
      </IconButton>
    </li>
  );
}

export function breakProblem(breaks: readonly SourceEditBreak[]): string | null {
  for (const b of breaks) {
    if (b.kind === "expected_quiet") continue;
    if (!b.label.trim() || !b.from || !b.to) return "Give each break a name, a start and an end";
    if (b.to < b.from) return "A break ends before it starts";
  }
  return null;
}

export function BreaksField({
  breaks,
  error,
  onChange,
}: {
  breaks: readonly SourceEditBreak[];
  error: string | null;
  onChange: (next: SourceEditBreak[]) => void;
}) {
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend className="contents">
        <FieldLabel optional="optional">Known breaks</FieldLabel>
      </legend>
      <ul className="m-0 mb-1.5 flex list-none flex-col gap-1.5 p-0">
        {breaks.map((b, i) => (
          <BreakRow
            key={b.id ?? `new-${String(i)}`}
            value={b}
            index={i}
            onChange={(next) => {
              onChange(breaks.map((x, j) => (j === i ? next : x)));
            }}
            onRemove={() => {
              onChange(breaks.filter((_, j) => j !== i));
            }}
          />
        ))}
      </ul>
      <Button
        size="sm"
        onClick={() => {
          onChange([...breaks, { id: null, kind: "recorded_break", from: "", to: null, label: "", ended: false }]);
        }}
      >
        <Plus aria-hidden="true" />
        Add a break
      </Button>
      {error ? <FieldError id="edit-source-breaks-err">{error}</FieldError> : null}
      <FieldHint>Recesses and summer breaks. Unusually quiet isn&apos;t raised during them.</FieldHint>
    </fieldset>
  );
}

export function ChangesBox({
  changes,
  noPlan,
  onUndo,
}: {
  changes: readonly SourceEditChange[];
  noPlan: boolean;
  onUndo: (key: string) => void;
}) {
  const warnings = changes.flatMap((c) => (c.warning ? [c.warning] : []));
  return (
    <section aria-label="Changes" className="mt-4 rounded-lg border border-divider px-3 py-2">
      <h3 className="m-0 mb-1 text-xs font-semibold tracking-[0.06em] text-muted uppercase">Changes</h3>
      {changes.length === 0 ? (
        <p className="m-0 text-md text-muted">Nothing changed yet.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-md">
          {changes.map((c) => (
            <li key={c.key} className="flex items-start gap-2">
              <span className="min-w-0 flex-1">
                <b className="font-strong text-fg">{c.label}</b>
                <span className="block text-muted">
                  <del>{c.before}</del> → <span className="text-fg">{c.after}</span>
                </span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Undo the change to ${c.label}`}
                onClick={() => {
                  onUndo(c.key);
                }}
              >
                Undo
              </Button>
            </li>
          ))}
        </ul>
      )}
      {warnings.map((w) => (
        <Callout key={w} tone="waiting" className="mt-2">
          {w}.
        </Callout>
      ))}
      {noPlan ? (
        <Callout tone="neutral" className="mt-2">
          Afterwards the source is in no plan: it shows Not in any plan and is no longer scanned. Archived items and
          evidence stay.
        </Callout>
      ) : null}
    </section>
  );
}

export function ConflictCallout({
  error,
  meanwhile,
  ready,
  onKeep,
  onDiscard,
}: {
  error: ApiError;
  meanwhile: readonly string[];
  ready: boolean;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  const loading = ready ? null : "Loading the saved version…";
  return (
    <Callout
      tone="waiting"
      role="alert"
      className="mt-3"
      title="This source changed since you opened it"
      actions={
        <>
          <Button size="sm" disabledReason={loading} onClick={onDiscard}>
            Use the saved version
          </Button>
          <Button size="sm" variant="primary" disabledReason={loading} onClick={onKeep}>
            Keep my draft
          </Button>
        </>
      }
    >
      {error.message}
      {error.changedAt && error.changedBy ? ` Saved ${formatDateTime(error.changedAt)}.` : ""}
      {meanwhile.length > 0 ? <span className="block">Changed meanwhile: {meanwhile.join(", ")}.</span> : null}
      <span className="block text-md text-muted">
        Keep my draft reviews your changes against the saved version; nothing is saved until you save again.
      </span>
    </Callout>
  );
}

export function sourceIsPlanless(view: SourceEditView, fields: SourceEditFields): boolean {
  return !view.shared_plan && !fields.people.some((p) => p.in_plan);
}
